import React, { forwardRef, useRef } from 'react'
import Icon, { IconName, IconColor } from 'particles/Icon'
import Text from 'particles/Text'
import { StyledTag, getTagFontColor } from './style'
import { useTheme } from '../themes/styled'
import { TagSeverity } from 'services/api/constants'
import Tooltip from 'particles/Tooltip'

export type TagState = 'default' | 'hover' | 'disable'
export type TagContentType = 'neutral' | 'positive' | 'warning' | 'negative'

export interface TagProps {
    label: string
    state?: TagState
    contentType?: TagContentType
    number?: number
    icon?: IconName
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
    isDisabled?: boolean
    maxWidth?: string
    customStyle?: React.CSSProperties
    isHoverable?: boolean
    showTooltipOverflow?: boolean
}

export const getSeverityState = (severity: TagSeverity | undefined): TagContentType => {
    if (severity === 'bad') {
        return 'negative'
    } else if (severity === 'average') {
        return 'warning'
    } else if (severity === 'good') {
        return 'positive'
    } else {
        return 'neutral'
    }
}

const RoundTag = forwardRef<HTMLButtonElement, TagProps>(
    (
        {
            state = 'default',
            contentType = 'neutral',
            label,
            number,
            icon,
            onClick,
            isDisabled = false,
            maxWidth = '224px',
            isHoverable = false,
            showTooltipOverflow = true,
            ...props
        },
        ref,
    ) => {
        const theme = useTheme()
        const labelRef = useRef<HTMLDivElement>(null)
        // prevent hover style if isHoverable is false
        const currentState = state === 'hover' && !isHoverable ? 'default' : state

        return (
            <Tooltip label={showTooltipOverflow ? label : undefined}>
                <StyledTag
                    ref={ref}
                    onClick={onClick}
                    state={currentState}
                    contentType={contentType}
                    label={label}
                    disabled={isDisabled}
                    maxWidth={maxWidth}
                    hasNumber={number !== undefined}
                    isHoverable={isHoverable}
                    {...props}
                >
                    {icon && (
                        <Icon
                            className={'tag-icon'}
                            name={icon}
                            size={'16px'}
                            color={getTagFontColor(isDisabled ? 'disable' : state, contentType, theme) as IconColor}
                        />
                    )}
                    <Text className="label" scale={'caption500'} ref={labelRef}>
                        {label}
                    </Text>

                    {number !== undefined && (
                        <Text className="number" scale={'caption500'}>
                            {number > 999 ? '999+' : number}
                        </Text>
                    )}
                </StyledTag>
            </Tooltip>
        )
    },
)

RoundTag.displayName = 'RoundTag'

export default RoundTag
